import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { Route } from '../types';

interface SearchInputProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  routes: Route[];
  className?: string;
}

export default function SearchInput({ 
  searchTerm, 
  onSearchChange, 
  routes, 
  className = '' 
}: SearchInputProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<Route[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Generate suggestions based on search term
  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = routes
      .filter(route => 
        route.Route.toLowerCase().includes(searchLower) ||
        route.Location.toLowerCase().includes(searchLower) ||
        String(route.Rating).toLowerCase().includes(searchLower)
      )
      .slice(0, 8) // Limit to 8 suggestions
      .sort((a, b) => {
        // Sort by relevance (exact matches first, then alphabetical)
        const aExact = a.Route.toLowerCase().startsWith(searchLower);
        const bExact = b.Route.toLowerCase().startsWith(searchLower);
        
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
        return a.Route.localeCompare(b.Route);
      });

    setSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
    setSelectedIndex(-1);
  }, [searchTerm, routes]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.target.value);
  };

  // Handle suggestion click
  const handleSuggestionClick = (route: Route) => {
    onSearchChange(route.Route);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search by route name or location..."
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          className={className}
        />
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((route, index) => (
            <div
              key={route.Route}
              onClick={() => handleSuggestionClick(route)}
              className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                index === selectedIndex
                  ? 'bg-primary-50 text-primary-700'
                  : 'hover:bg-gray-50 text-gray-800'
              }`}
            >
              <div className="font-medium text-sm">{route.Route}</div>
              <div className="text-xs text-gray-500 mt-1">
                {route['Route Type']} • {route.Rating} • {route.Length} ft
              </div>
            </div>
          ))}
          
          {/* Show more indicator if there are many matches */}
          {suggestions.length === 8 && (
            <div className="px-4 py-2 text-xs text-gray-500 text-center border-t border-gray-100">
              Type more to narrow results...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
